import Spacer from '@/common/components/spacer';

import SavingsCalculatorBlobContainer from './savings-calculator-blob-container';
import { getDynamicCurrency } from '@/util-conversion/currency/currency-to-string';
import SavingsReceipt from './savings-receipt';
import { VehicleSavingsCalculatorV2 } from '@/catalog/types/vehicle';
import SavingsReceiptContainer from './savings-receipt-container';
import SavingsRules from './savings-rule';
import MagnifyingGlass from '@/icons/magnifying-glass';
import { pluralize } from '@/common/utils/pluralize';
import SavingsCalculatorCollapsibleSection from './savings-calculator-collapsible-section';

interface SavingsMathSectionProps {
  savings: VehicleSavingsCalculatorV2;
  ownershipYears: number;
  planMileage: number;
  originMarketName?: string;
  destinationMarketName?: string;
}

export default function SavingsMathSection({
  savings,
  ownershipYears,
  planMileage,
  originMarketName,
  destinationMarketName,
}: SavingsMathSectionProps) {
  return (
    <SavingsCalculatorBlobContainer className="py-[16px]">
      <SavingsCalculatorCollapsibleSection
        title="The math behind it all"
        titleIcon={<MagnifyingGlass className="h-[22px] w-[25px] flex-shrink-0" />}
        subTrigger={
          <p className="body2 text-left font-sans">
            We break down every cost for buying vs. Flexcar to show how your savings add
            up.
          </p>
        }
        keepSubTriggerVisible
      >
        <Spacer variant="height24" />
        <SavingsReceiptContainer variant="buying">
          <SavingsReceipt
            totalTitle=""
            lineItems={[
              {
                description: 'Wholesale price',
                price: getDynamicCurrency(savings.upfront_wholesale_price),
                isStrong: true,
              },
              {
                description: 'Retail markup',
                price: getDynamicCurrency(savings.upfront_retail_markup),
                bottomSpacerHeight: 8,
              },
              {
                description: 'Dealer advertised price',
                price: getDynamicCurrency(savings.upfront_advertised_price),
                isPseudoSubTotal: true,
              },
              {
                description: 'Transaction fees',
                price: getDynamicCurrency(0),
                suppressPriceDisplay: true,
                subLineItems: [
                  {
                    description: 'Documentation fees',
                    price: getDynamicCurrency(savings.upfront_documentation_fees),
                  },
                  {
                    description: 'Title, Registration, Inspection',
                    price: getDynamicCurrency(
                      savings.upfront_title_registration_inspection_fee
                    ),
                  },
                  {
                    description: 'Vehicle prep fee',
                    price: getDynamicCurrency(savings.upfront_vehicle_prep_fee),
                  },
                ],
              },
              {
                description: 'Other dealer fees',
                price: getDynamicCurrency(0),
                suppressPriceDisplay: true,
                subLineItems: [
                  {
                    description: 'Extended warranty',
                    subDescription: '(coverage included with Flexcar)',
                    price: getDynamicCurrency(savings.upfront_extended_warranty_fee),
                    subLineItemTooltipDescription: `Extended car warranties cover malfunctions in a car's engine and drivetrain beyond the standard warranty period.`,
                  },
                  {
                    description: 'Tire protection',
                    subDescription: '(coverage included with Flexcar)',
                    price: getDynamicCurrency(
                      savings.upfront_tire_and_wheel_protection_fee
                    ),
                  },
                ],
              },
              {
                description: 'Financing fees & taxes',
                price: getDynamicCurrency(savings.upfront_financing_fees),
                suppressPriceDisplay: true,
                subLineItems: [
                  {
                    description: 'Application fee',
                    price: getDynamicCurrency(savings.upfront_application_fee),
                  },
                  {
                    description: 'Gap insurance',
                    price: getDynamicCurrency(savings.upfront_gap_insurance_fee),
                  },
                  {
                    description: `Sales tax (${savings.sales_tax}%)`,
                    price: getDynamicCurrency(savings.upfront_sales_tax),
                  },
                ],
              },
            ]}
            totalLineItem={{
              description: 'Total purchase price',
              isStrong: true,
              price: getDynamicCurrency(savings.upfront_total_purchase_price),
            }}
            totalPrice={savings.upfront_total_purchase_price}
            wholesalePrice={savings.upfront_wholesale_price}
            dayOneLossAmount={savings.upfront_day_one_loss_amount}
          />

          <SavingsRules color="bg-flexcar-neutral100" />

          <SavingsReceipt
            totalTitle="Upfront cost of buying"
            lineItems={[
              {
                description: `Down payment (${savings.down_payment_percentage}%)`,
                price: getDynamicCurrency(savings.upfront_down_payment),
                isStrong: true,
              },
            ]}
          />

          <SavingsRules color="bg-flexcar-neutral100" />

          <SavingsReceipt
            totalTitle="Monthly recurring cost of buying"
            lineItems={[
              {
                description: 'Vehicle payment',
                price: `${getDynamicCurrency(savings.monthly_vehicle_payment)}/mo`,
                subLineItems: [
                  {
                    description: 'Total financed',
                    price: getDynamicCurrency(savings.monthly_total_financed),
                  },
                  {
                    description: 'Interest rate',
                    price: `${savings.interest_rate}% for ${savings.loan_term_months} months`,
                  },
                ],
              },
              {
                description: 'Insurance [Bankrate, Car Edge]',
                price: `${getDynamicCurrency(savings.monthly_insurance)}/mo`,
              },
              {
                description: 'Maintenance [Car Edge]',
                price: `${getDynamicCurrency(savings.monthly_maintenance)}/mo`,
              },
              {
                description: 'Regulatory fees',
                price: `${getDynamicCurrency(savings.monthly_regulatory_fees)}/mo`,
                subLineItems: [
                  {
                    description: 'Registration',
                    price: getDynamicCurrency(savings.monthly_registration_fee),
                  },
                  {
                    description: 'Emissions inspection',
                    price: getDynamicCurrency(savings.monthly_emissions_inspection_fee),
                  },
                  // If excise tax is 0, don't show it
                  ...(savings.monthly_excise_tax !== 0
                    ? [
                        {
                          description: `Monthly excise${savings.excise_tax ? ` (${savings.excise_tax}%)` : ''}`,
                          price: getDynamicCurrency(savings.monthly_excise_tax),
                        },
                      ]
                    : []),
                ],
              },
              {
                description: 'Roadside assistance',
                price: `${getDynamicCurrency(savings.monthly_roadside_assistance)}/mo`,
              },
            ]}
            totalLineItem={{
              description: 'Monthly total',
              price: `${getDynamicCurrency(savings.monthly_buying_cost)}/mo`,
            }}
          />
          <SavingsRules color="bg-flexcar-neutral100" />
          <SavingsReceipt
            totalTitle={`Total cost over ${ownershipYears} ${pluralize(ownershipYears, 'year')}`}
            lineItems={[
              {
                description: 'Down payment',
                price: getDynamicCurrency(savings.ownership_year_term_down_payment),
              },
              {
                description: 'Total monthly recurring',
                price: getDynamicCurrency(savings.ownership_year_term_recurring_monthly),
              },
            ]}
            subTotalLineItems={[
              {
                description: `Buying ${ownershipYears}-year total`,
                price: getDynamicCurrency(savings.ownership_year_term_buying_cost),
                isStrong: true,
              },
              {
                description: `Estimated trade-in value in ${savings.ownership_years_to_date}`,
                price:
                  savings.ownership_year_term_trade_in_value < 0
                    ? `${getDynamicCurrency(Math.abs(savings.ownership_year_term_trade_in_value))}`
                    : `-${getDynamicCurrency(savings.ownership_year_term_trade_in_value)}`,
              },
              {
                description: `Remaining Loan Balance`,
                price:
                  savings.ownership_year_remaining_loan_balance < 0
                    ? `-${getDynamicCurrency(Math.abs(savings.ownership_year_remaining_loan_balance))}`
                    : `${getDynamicCurrency(savings.ownership_year_remaining_loan_balance)}`,
              },
            ]}
            totalLineItem={{
              description: `Total after car sale`,
              price: getDynamicCurrency(savings.ownership_year_term_total_after_car_sale),
              hasSalesTaxApplied: true,
            }}
          />
        </SavingsReceiptContainer>
        <Spacer variant="height24" />
        <SavingsReceiptContainer variant="flexcar">
          <SavingsReceipt
            totalTitle="Upfront cost"
            lineItems={[
              {
                description: `Annual membership`,
                price: `${getDynamicCurrency(savings.flexcar_annual_membership_pre_tax)}/yr`,
              },
              ...(savings.flexcar_shipping_fee_pre_tax > 0
                ? [
                    {
                      description: `Shipping fee`,
                      price: getDynamicCurrency(savings.flexcar_shipping_fee_pre_tax),
                      lineItemTooltipTitle:
                        originMarketName && destinationMarketName
                          ? `${originMarketName} -> ${destinationMarketName}`
                          : 'Shipping fee',
                      lineItemTooltipDescription:
                        'This one-time fee covers the cost of shipping the vehicle to your closest Flexcar location.',
                    },
                  ]
                : []),
              ...(savings.flexcar_new_vehicle_fee_pre_tax > 0
                ? [
                    {
                      description: `New car surcharge`,
                      price: getDynamicCurrency(savings.flexcar_new_vehicle_fee_pre_tax),
                    },
                  ]
                : []),
            ]}
          />
          <SavingsRules color="bg-flexcar-neutral90" />
          <SavingsReceipt
            totalTitle="Monthly vehicle cost"
            {...(planMileage === 0
              ? {
                  tooltipDescription:
                    'Calculated using Standard Commuter for 6 months then Occasional Driver for 6 months',
                }
              : {})}
            lineItems={[
              {
                description: `${pluralize(ownershipYears, 'Year')} 1${ownershipYears === 1 ? '' : ' & 2'}`,
                price: `${getDynamicCurrency(savings.flexcar_year_one_and_two_pre_tax)}/mo`,
              },
              ...(ownershipYears >= 3
                ? [
                    {
                      description: 'Years 3 & 4 (expected price)',
                      price: `${getDynamicCurrency(savings.flexcar_year_three_and_four_pre_tax)}/mo`,
                    },
                  ]
                : []),
              ...(ownershipYears >= 5
                ? [
                    {
                      description: `Year 5${ownershipYears > 5 ? ' & 6' : ''} (expected price)`,
                      price: `${getDynamicCurrency(savings.flexcar_year_five_pre_tax)}/mo`,
                    },
                  ]
                : []),
            ]}
          />
          <SavingsRules color="bg-flexcar-neutral90" />
          <SavingsReceipt
            totalTitle="Monthly insurance cost"
            lineItems={[
              {
                description: `Essential Protection`,
                price: `${getDynamicCurrency(savings.flexcar_monthly_insurance_pre_tax)}/mo`,
              },
            ]}
          />
          <SavingsRules color="bg-flexcar-neutral90" />
          <SavingsReceipt
            totalTitle={`Total cost over ${ownershipYears} ${pluralize(ownershipYears, 'year')} (incl. taxes)`}
            lineItems={[
              {
                description: `Annual membership`,
                price: getDynamicCurrency(
                  savings.flexcar_annual_membership_ownership_year_term
                ),
              },
              ...(savings.flexcar_shipping_fee > 0
                ? [
                    {
                      description: `One-time shipping fee`,
                      price: getDynamicCurrency(savings.flexcar_shipping_fee),
                    },
                  ]
                : []),
              ...(savings.flexcar_new_vehicle_fee > 0
                ? [
                    {
                      description: `One-time new car surcharge`,
                      price: getDynamicCurrency(savings.flexcar_new_vehicle_fee),
                    },
                  ]
                : []),
              {
                description: 'Total monthly recurring',
                price: getDynamicCurrency(
                  savings.flexcar_ownership_year_term_total_monthly_recurring
                ),
              },
            ]}
            totalLineItem={{
              description: `Flexcar ${ownershipYears}-year total`,
              price: getDynamicCurrency(savings.flexcar_ownership_year_term_total),
              hasSalesTaxApplied: true,
            }}
          />
        </SavingsReceiptContainer>
      </SavingsCalculatorCollapsibleSection>
    </SavingsCalculatorBlobContainer>
  );
}
